// @wc-ignore-file
// (Refusal keys, error codes and query patterns are data, not copy.)

import { KEY_ARROW_DOWN, KEY_ARROW_UP, KEY_END, KEY_ENTER, KEY_HOME } from "@/lib/keyboard-keys";
import type { CrDependencyListRow } from "@shared/types/supabase.types";

/**
 * CR-148: a search query that names a CR by number — `12`, `CR-12`, `cr12`,
 * `#12`. Returns the number, or null for anything else. Capped at 9 digits so
 * the value always fits the `integer` sequential_id column (a larger literal
 * would make PostgREST fail the whole search instead of matching nothing).
 * Only a string this returns non-null for is ever interpolated into a
 * PostgREST `or()` filter, so the pattern doubles as the injection guard.
 */
export function parseCrNumberQuery(query: string): number | null {
  const match = /^(?:cr[-\s]?|#)?(\d{1,9})$/i.exec(query.trim());
  if (!match) {
    return null;
  }
  const value = Number(match[1]);
  return value >= 1 ? value : null;
}

/**
 * Why an add / remove was refused, from the stage-1 RPC exception keys. A key
 * the client does not know (or a network failure) is `unknown` — the UI shows
 * a generic line rather than guessing.
 */
export type CrDependencyErrorReason =
  | "not_found"
  | "source_archived"
  | "self"
  | "done"
  | "cycle"
  | "exists"
  | "not_active"
  | "permission"
  | "unknown";

// Keep in sync with the MCP copy (backend/api-server/src/lib/mcp-cr-dependencies.ts).
const REFUSAL_KEYS: Record<string, CrDependencyErrorReason> = {
  cr_dependency_not_found: "not_found",
  cr_dependency_source_archived: "source_archived",
  cr_dependency_self: "self",
  cr_dependency_done: "done",
  cr_dependency_cycle: "cycle",
  cr_dependency_exists: "exists",
  cr_dependency_not_active: "not_active",
};

export function crDependencyErrorReason(error: unknown): CrDependencyErrorReason {
  if (typeof error !== "object" || error === null) {
    return "unknown";
  }
  const { code, message } = error as { code?: unknown; message?: unknown };
  if (code === "42501") {
    return "permission";
  }
  if (typeof message === "string" && Object.hasOwn(REFUSAL_KEYS, message)) {
    return REFUSAL_KEYS[message] ?? "unknown";
  }
  return "unknown";
}

/**
 * Why an inactive row is history rather than a blocker. A manual removal wins
 * (it is what the user did), then archiving, then completion — the same order
 * the server's `is_active` rule checks them in.
 */
export type CrDependencyHistoryKind = "removed" | "archived" | "completed";

export function crDependencyHistoryKind(row: CrDependencyListRow): CrDependencyHistoryKind | null {
  if (row.is_active) {
    return null;
  }
  if (row.removed_at) {
    return "removed";
  }
  if (row.deleted_at) {
    return "archived";
  }
  return "completed";
}

/**
 * What a key press in the dependency search box means to the editor. Key
 * names come from `@/lib/keyboard-keys` (CR-170): an inline key literal would
 * be extracted and translated by wuchale and never match.
 */
export function crDependencySearchKeyAction(key: string): "submit" | "navigate" | null {
  if (key === KEY_ENTER) {
    return "submit";
  }
  if (key === KEY_ARROW_DOWN || key === KEY_ARROW_UP || key === KEY_HOME || key === KEY_END) {
    return "navigate";
  }
  return null;
}
